import {
	on as subscribeToEvent,
	emit as dispatchEvent,
	type Subscription,
} from '../../custom-ui/bridge/methods/events';
import type { BridgeMutableState, MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

// ─── Context slices ───────────────────────────────────────────────────────────

type OnContext = MakeBridgeContext<'extension' | 'extensionDetails' | 'mutableState'>;
type EmitContext = MakeBridgeContext<'extensionDetails'>;

// ─── Payload / result shapes ──────────────────────────────────────────────────

type EventCallbackPayload = {
	callback: (payload?: any) => Promise<any>;
	event: string;
};

type EmitPayload = {
	event: string;
	payload?: any;
};

type SubscriptionHandle = {
	unsubscribe: () => void;
};

// ─── Shared helpers ───────────────────────────────────────────────────────────────────────

/**
 * Registers a Subscription in the mutableState list and returns a handle whose
 * `unsubscribe` method removes it from the list and tears down the listener.
 *
 * Using `indexOf` + `splice` rather than `filter` keeps the original array
 * reference stable so cleanup effects on unmount always see the live list.
 */
const trackSubscription = (
	subscription: Subscription,
	mutableState: BridgeMutableState,
): SubscriptionHandle => {
	mutableState.eventSubscriptions.push(subscription);
	return {
		unsubscribe: () => {
			const index = mutableState.eventSubscriptions.indexOf(subscription);
			if (index !== -1) {
				mutableState.eventSubscriptions.splice(index, 1);
			}
			subscription.unsubscribe();
		},
	};
};

// ─── Bridge methods ───────────────────────────────────────────────────────────

/**
 * Subscribes to a forge.bridge DOM event scoped to the current extension.
 *
 * Registers the underlying Subscription in `mutableState.eventSubscriptions` so
 * the host can batch-unsubscribe all listeners on unmount. The returned handle's
 * `unsubscribe` removes the entry from the list before tearing down the listener.
 */
export const on: TBridgeMethod<OnContext, EventCallbackPayload, SubscriptionHandle> = async (
	{ extensionDetails, mutableState },
	{ event, callback },
) => {
	const subscription = subscribeToEvent(event, callback, extensionDetails);
	return trackSubscription(subscription, mutableState);
};

/**
 * Dispatches a forge.bridge event
 */
export const emit: TBridgeMethod<EmitContext, EmitPayload, void> = async ({ extensionDetails }, { event, payload }) => {
	dispatchEvent(event, payload, extensionDetails);
};
